import React, { useState, useEffect } from 'react';
import { Syringe, Paperclip, Camera, AlertCircle, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { healthService } from '@/services/healthService';
import { cycleService } from '@/services/cycleService';
import {
  MedicineProductRecord,
  ProductionCycleRecord,
} from '@/types/database';

interface HealthEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (event: any) => void;
  cycleId?: string | null;
  cycleCode?: string;
}

export const HealthEventModal: React.FC<HealthEventModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  cycleCode,
}) => {
  const [selectedCycleId, setSelectedCycleId] = useState<string>(cycleId || '');
  const [eventDate, setEventDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [eventType, setEventType] = useState<string>('ASF Diagnostic Test');
  const [customEventType, setCustomEventType] = useState<string>('');
  const [diagnosticResult, setDiagnosticResult] = useState<string>('Negative (ASF-Free)');
  const [customDiagnosticResult, setCustomDiagnosticResult] = useState<string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [headCountTreated, setHeadCountTreated] = useState<number | ''>('');
  const [totalCost, setTotalCost] = useState<number | ''>('');
  const [vetTechnician, setVetTechnician] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);
  const [medicines, setMedicines] = useState<MedicineProductRecord[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const suggestedEventTypes = healthService.getSuggestedEventTypes();
  const suggestedResults = healthService.getSuggestedDiagnosticResults();

  useEffect(() => {
    if (cycleId) setSelectedCycleId(cycleId);
  }, [cycleId]);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);

    Promise.all([
      cycleService.getCycles({ status: 'active' }),
      healthService.getMedicineProducts(),
    ]).then(([cycs, meds]) => {
      setCycles(cycs);
      setMedicines(meds);

      if (cycs.length > 0 && !selectedCycleId && !cycleId) {
        setSelectedCycleId(cycs[0].id);
      }
    });
  }, [isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAttachedFile(e.target.files[0]);
    }
  };

  const finalEventType = eventType === 'Custom / Other' ? customEventType : eventType;
  const finalDiagnosticResult =
    diagnosticResult === 'Custom' ? customDiagnosticResult : diagnosticResult;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const targetCycleId = cycleId || selectedCycleId;
    if (!targetCycleId) {
      setErrorMsg('Please select a Production Cycle.');
      return;
    }
    if (!eventDate) {
      setErrorMsg('Event Date is required.');
      return;
    }
    if (!finalEventType || !finalEventType.trim()) {
      setErrorMsg('Event Type / Description is required.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        cycle_id: targetCycleId,
        event_date: eventDate,
        event_type: finalEventType.trim(),
        product_id: selectedProductId || null,
        head_count_treated: headCountTreated !== '' ? Number(headCountTreated) : null,
        total_cost: totalCost !== '' ? Number(totalCost) : 0,
        diagnostic_result: finalDiagnosticResult ? finalDiagnosticResult.trim() : null,
        veterinarian_technician: vetTechnician.trim() || null,
        notes: notes.trim() || null,
      };

      const result = await healthService.createHealthEvent(payload, attachedFile);
      onSuccess(result);
      onClose();
    } catch (err: any) {
      console.error('Error logging health event:', err);
      setErrorMsg(err.message || 'Failed to log health event.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col p-4 sm:p-6 overflow-hidden">
        <DialogHeader className="pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-800 flex items-center justify-center">
              <Syringe className="w-4 h-4" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900">
                Log Health / Treatment / ASF Event
              </DialogTitle>
              <p className="text-xs text-slate-500">
                Record diagnostic testing, biosecurity lab certificates, or medical treatments.
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto py-3 space-y-4 no-scrollbar">
          {errorMsg && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Destination Production Cycle */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Destination Production Cycle *</label>
            {cycleCode ? (
              <div className="p-2.5 rounded-md bg-slate-100 border border-slate-200 text-xs font-mono font-bold text-slate-800">
                {cycleCode}
              </div>
            ) : (
              <select
                value={selectedCycleId}
                onChange={(e) => setSelectedCycleId(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
                required
              >
                <option value="">-- Select Active Production Cycle --</option>
                {cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.cycle_code} ({c.growers?.full_name || 'Company-Owned'})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Event Date & Event Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Event Date *</label>
              <Input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="h-9 text-xs"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">
                Event Classification / Description *
              </label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
                required
              >
                {suggestedEventTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                <option value="Custom / Other">Custom / Other Description...</option>
              </select>
            </div>
          </div>

          {eventType === 'Custom / Other' && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Custom Event Description *</label>
              <Input
                type="text"
                value={customEventType}
                onChange={(e) => setCustomEventType(e.target.value)}
                placeholder="Enter specific actual event description"
                className="h-9 text-xs"
                required
              />
            </div>
          )}

          {/* Diagnostic Result (Flexible suggestion with arbitrary custom text support) */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">
              Diagnostic Result / ASF Status (Suggested or Custom)
            </label>
            <select
              value={diagnosticResult}
              onChange={(e) => setDiagnosticResult(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
            >
              <option value="">-- No Diagnostic Test Performed --</option>
              {suggestedResults.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
              <option value="Custom">Custom Lab Result / Finding...</option>
            </select>
          </div>

          {diagnosticResult === 'Custom' && (
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Custom Diagnostic Findings</label>
              <Input
                type="text"
                value={customDiagnosticResult}
                onChange={(e) => setCustomDiagnosticResult(e.target.value)}
                placeholder="Enter specific lab finding or status"
                className="h-9 text-xs"
              />
            </div>
          )}

          {/* Medicine Product Used (Optional) */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Administered Medicine / Product (Optional)</label>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
            >
              <option value="">-- None / Non-Pharmaceutical Event --</option>
              {medicines.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.product_name} {m.category ? `(${m.category})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Head Count Treated & Total Cost */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Head Count Treated</label>
              <Input
                type="number"
                step="1"
                inputMode="numeric"
                value={headCountTreated}
                onChange={(e) => setHeadCountTreated(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g. 50"
                className="h-9 text-xs font-mono"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700">Total Treatment Cost (₱)</label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={totalCost}
                onChange={(e) => setTotalCost(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="₱0.00"
                className="h-9 text-xs font-mono"
              />
            </div>
          </div>

          {/* Veterinarian / Technician */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Attending Vet / Technician</label>
            <Input
              type="text"
              value={vetTechnician}
              onChange={(e) => setVetTechnician(e.target.value)}
              placeholder="e.g. Dr. Santos / Farm Vet"
              className="h-9 text-xs"
            />
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Observations / Dosage / Notes</label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Blood samples submitted to BAI laboratory; routine 2nd booster"
              className="h-9 text-xs"
            />
          </div>

          {/* Diagnostic Certificate Photo / Document Upload */}
          <div className="space-y-1.5 p-3 rounded-lg border border-slate-200 bg-slate-50">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-slate-500" />
              Diagnostic Certificate / Lab Result / Photo (WP07 Secure Storage)
            </label>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 shadow-xs">
                <Camera className="w-3.5 h-3.5 text-slate-600" />
                <span>{attachedFile ? 'Change Attachment' : 'Take Photo / Upload Certificate'}</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  capture="environment"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              {attachedFile && (
                <span className="text-xs font-mono text-emerald-700 truncate max-w-[200px]">
                  ✓ {attachedFile.name}
                </span>
              )}
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-slate-200 flex flex-row items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="bg-teal-700 hover:bg-teal-800 text-white font-semibold"
            >
              {loading ? 'Saving Event...' : 'Save Health Event'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
